import { joinQueue, autoJoinRemaining } from '../game/GameConfig.js';
import { MarqueeManager } from '../ui/MarqueeManager.js';
import { socketService } from '../services/SocketService.js';
import sessionStateManager from '../game/SessionStateManager.js';

function getAutoJoinRecord(userId) {
  const existing = autoJoinRemaining.get(userId);
  if (existing && typeof existing === 'object') {
    const credits = Number.isFinite(existing.credits) ? Number(existing.credits) : 0;
    const normalized = { ...existing, credits };
    autoJoinRemaining.set(userId, normalized);
    return normalized;
  }
  const credits = Number.isFinite(existing) ? Number(existing) : Number(existing || 0);
  const normalized = { credits: Number.isFinite(credits) ? credits : 0 };
  if (userId != null) {
    autoJoinRemaining.set(userId, normalized);
  }
  return normalized;
}

function updateAutoJoinRecord(userId, updates = {}) {
  if (userId == null) {
    return null;
  }
  const base = getAutoJoinRecord(userId);
  const next = { ...base, ...updates };
  if (!Number.isFinite(next.credits)) {
    next.credits = Number(base.credits) || 0;
  }
  autoJoinRemaining.set(userId, next);
  return next;
}


class GlobalChatHandler {
  constructor() {
    this.isInitialized = false;
    this.init();
  }

  init() {
    if (this.isInitialized) return;

    // Listen for chat messages globally
    window.addEventListener('soulwars-chat', this.handleGlobalChat.bind(this));
    this.isInitialized = true;
    console.log('[GlobalChatHandler] Initialized global chat handler');
  }

  async handleGlobalChat(ev) {
    if (!ev || !ev.detail) return;

    const m = ev.detail;
    const message = (m.message || '').toLowerCase().trim();

    // Handle join commands globally
    if (message === 'join' || message === 'play') {
      await this.handleJoinRequest(m);
    }

    // Handle buy commands globally, if the message contains "buy"
    if (message.includes('buy')) {
      //await this.handleBuyRequest(m);
    }
  }

  //buy request could be handled by the JoinShopManager or the PreBattleScene
  async handleBuyRequest(chatMessage) {
    console.log('[GlobalChatHandler] handleBuyRequest', chatMessage);
    const wallet = chatMessage.userAddress;
    const username = chatMessage.username || 'Guest';
    const platform = (chatMessage.platform || 'local').toLowerCase();
  }

  async handleJoinRequest(chatMessage) {

    console.log('[GlobalChatHandler] handleJoinRequest', chatMessage);
    const wallet = chatMessage.userAddress;
    const username = chatMessage.username || 'Guest';
    const platform = (chatMessage.platform || 'local').toLowerCase();
    const userId = chatMessage.userId;

    /*console.log('[GlobalChatHandler] wallet', wallet);
    console.log('[GlobalChatHandler] username', username);
    console.log('[GlobalChatHandler] platform', platform);
    console.log('[GlobalChatHandler] userId', userId);*/

    if (!userId) {
      console.warn('[GlobalChatHandler] No userId for join request - user may not have linked account');
      return;
    }

    // Grant 3 auto-join credits
    const record = getAutoJoinRecord(userId);
    const nextCredits = Math.max(3, Number(record.credits) || 0);
    updateAutoJoinRecord(userId, {
      credits: nextCredits,
      lastPlatform: platform || record.lastPlatform || null,
      lastWallet: wallet || record.lastWallet || null,
      lastUsername: username || record.lastUsername || null,
      lastJoinSource: 'chat',
      lastJoinAt: Date.now()
    });
    console.log(`🎫 Auto-join credits granted: user ${userId} now has ${nextCredits} credits`);

    // Check if JoinScene is currently active
    const game = window.phaserGameRef?.current;
    let joinScene = null;

    if (game && game.scene) {
      // Use getScene method to get the scene plugin, then access the actual scene instance
      const scenePlugin = game.scene.getScene('JoinScene');
      
      // Get the actual scene instance from the plugin
      if (scenePlugin && scenePlugin.scene) {
        const sceneInstance = scenePlugin.scene;
        // Check if the scene is active (isActive is on the scene instance, accessed via plugin)
        if (scenePlugin.scene.isActive()) {
          joinScene = sceneInstance; // Use the actual scene instance which has addPlayer method
        }
      }
      
      console.log('[GlobalChatHandler] JoinScene found:', !!joinScene);
      console.log('[GlobalChatHandler] JoinScene is active:', !!joinScene);
    } else {
      if(game) {
        console.log('[GlobalChatHandler] Game:', game);
      } else {
        console.log('[GlobalChatHandler] No game found');
      }
      console.log('[GlobalChatHandler] No game scene found');
    }

    if (joinScene && typeof joinScene.addPlayer === 'function') {
      if (joinScene.playersLockedForVoting) {
        console.log('[GlobalChatHandler] JoinScene active but locked for voting - will queue join request');
      } else {
        // JoinScene is active - add player directly
        console.log('[GlobalChatHandler] JoinScene is active, adding player directly');

        try {
        // Fetch user and hero data
        const userResponse = await fetch(`/api/users/${userId}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
          }
        });

        // Build query params for hero creation if needed
        const heroParams = new URLSearchParams();
        heroParams.append('createIfNone', 'true');
        if (chatMessage.authorAvatar && platform === 'x') {
          heroParams.append('avatarUrl', chatMessage.authorAvatar);
        }
        
        const heroesResponse = await fetch(`/api/heroes/user/${userId}?${heroParams.toString()}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
          }
        });



        let enrichedData = {
          username: username,
          walletAddress: wallet,
          platform: platform,
          userId: userId,
          heroId: null,
          heroName: username,
          heroLevel: 1,
          sprite: null,
          credits: 0
        };

        // Get user data for username/wallet info
        if (userResponse.ok) {
          const userData = await userResponse.json();
          enrichedData.username = userData.chat_name || userData.username || username;
          enrichedData.walletAddress = userData.solana_wallet_address || wallet;
        }

        // Get fresh hero data
        let selectedHero = null;
        if (heroesResponse.ok) {
          const heroesData = await heroesResponse.json();
          if (heroesData && heroesData.data && heroesData.data.length > 0) {
            try {
              // Select hero based on battle frequency settings
              selectedHero = this.selectHeroByFrequency(heroesData.data);
              enrichedData.heroId = selectedHero.id;
              enrichedData.heroName = selectedHero.name;
              enrichedData.heroLevel = selectedHero.level || 1;
              enrichedData.sprite = selectedHero.image_url;
              enrichedData.health = selectedHero.health || 100;
              enrichedData.attack = selectedHero.attack || 50;
              enrichedData.defense = selectedHero.defense || 30;
              enrichedData.speed = selectedHero.speed || 40;
              enrichedData.quickness = selectedHero.quickness || 10;
              enrichedData.coordination = selectedHero.coordination || 10;
              enrichedData.strength = selectedHero.strength || 10;
              enrichedData.endurance = selectedHero.endurance || 10;
              enrichedData.focus = selectedHero.focus || 10;
              enrichedData.self = selectedHero.self || 10;
              enrichedData.mana = selectedHero.mana || 100;
              enrichedData.stamina = selectedHero.stamina || 100;
              enrichedData.inventory = selectedHero.inventory || null;
            } catch (heroSelectionError) {
              // Handle frequency 0 heroes - send error message and return early
              console.warn('[GlobalChatHandler] Hero selection failed:', heroSelectionError.message);
              this.sendConfirmationEmote(wallet, '❌');
              MarqueeManager.addMessage(`❌ ${username} cannot join - all heroes set to never join`, {
                color: '#EF4444'
              }, 'medium');
              return; // Don't proceed with join
            }
          }
        }

        // Ensure heroName is set from the selected hero, not from username/wallet
        if (selectedHero && selectedHero.name && !enrichedData.heroName) {
          enrichedData.heroName = selectedHero.name;
          console.log('[GlobalChatHandler] Set heroName from selectedHero:', selectedHero.name);
        }

        console.log('[GlobalChatHandler] Adding player directly to active JoinScene:', enrichedData);
        const added = joinScene.addPlayer(enrichedData);

        if (added) {
          // Track hero in session on server side via socket
          if (enrichedData.heroId && enrichedData.userId && enrichedData.heroName) {
            const sessionId = sessionStateManager.getSessionId();
            if (sessionId && socketService) {
              socketService.addSessionHero(
                sessionId,
                enrichedData.heroId,
                enrichedData.userId,
                enrichedData.heroName,
                false // isAutoJoin = false for pumpfun/chat joins
              );
              console.log(`[GlobalChatHandler] Tracked hero ${enrichedData.heroName} (${enrichedData.heroId}) in session ${sessionId} via socket`);
              
              // Request fresh inventory refresh via socket
              if (socketService.refreshHeroInventory) {
                socketService.refreshHeroInventory(enrichedData.heroId, sessionId);
                console.log(`[GlobalChatHandler] Requested inventory refresh for hero ${enrichedData.heroId}`);
              }
            }
          }

          // Send confirmation emote
          this.sendConfirmationEmote(wallet, '✅');

          // Show marquee message for successful join
          MarqueeManager.addMessage(`✅ ${username} joined the battle!`, {
            color: '#10B981'
          }, 'medium');

          return; // Player added directly, no need to queue
        } else {
          console.log('[GlobalChatHandler] addPlayer returned false, will queue join request');
        }

        } catch (error) {
          console.error('[GlobalChatHandler] Error adding player directly:', error);
          // Fall through to queue logic if direct addition fails
        }
      }
    }

    // JoinScene is not active or direct addition failed - add to queue
    console.log('[GlobalChatHandler] JoinScene not active, adding to join queue');

    // Create join request object
    const joinRequest = {
      userId: userId,
      username: username,
      platform: platform,
      timestamp: Date.now(),
      wallet: wallet,
      authorAvatar: chatMessage.authorAvatar, // Include avatar for X users
      retryCount: 0
    };

    // Check if this user is already in the queue
    const existingIndex = joinQueue.findIndex(req => req.userId === userId);
    if (existingIndex >= 0) {
      const existing = joinQueue[existingIndex] || {};
      joinQueue[existingIndex] = {
        ...existing,
        ...joinRequest,
        retryCount: existing.retryCount ?? 0
      };
      console.log('[GlobalChatHandler] Updated existing join queue entry for user:', userId);
    } else {
      joinQueue.push(joinRequest);
      console.log('[GlobalChatHandler] Added to join queue:', joinRequest);
    }

    // Send confirmation emote
    this.sendConfirmationEmote(wallet, '⏳');

    // Show marquee message for queued join
    MarqueeManager.addMessage(`⏳ ${username} queued for next battle`, {
      color: '#F59E0B'
    }, 'medium');
  }

  /**
   * Select a hero based on battle frequency settings
   * @param {Array} heroes - Array of hero objects
   * @returns {Object} Selected hero
   */
  selectHeroByFrequency(heroes) {
    if (!heroes || heroes.length === 0) {
      throw new Error('No heroes available');
    }

    // If only one hero, return it
    if (heroes.length === 1) {
      return heroes[0];
    }

    // Try to find an active hero first (preferred)
    const activeHero = heroes.find(h => h.is_active);
    if (activeHero) {
      return activeHero;
    }

    // Create weighted selection based on frequency
    const weightedHeroes = [];

    for (const hero of heroes) {
      // Get frequency value, default to 6 (normal) if not set
      let frequency = hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6;

      // Convert frequency to weight (higher frequency = higher weight)
      // Frequency scale: 0=never, 3=rare, 6=normal, 11=often
      let weight = 0;
      if (frequency === 0) {
        weight = 0; // Never select
      } else if (frequency === 3) {
        weight = 1; // Rare
      } else if (frequency === 6) {
        weight = 2; // Normal
      } else if (frequency === 11) {
        weight = 3; // Often
      } else {
        // Handle other values as normal
        weight = 2;
      }

      // Add hero to weighted pool based on weight
      for (let i = 0; i < weight; i++) {
        weightedHeroes.push(hero);
      }
    }

    // If no heroes have weight (all frequency 0), reject the join
    if (weightedHeroes.length === 0) {
      console.warn('[GlobalChatHandler] All heroes have frequency 0 (never), cannot join battle');
      throw new Error('No heroes available for battle - all heroes set to never join');
    }

    // Randomly select from weighted pool
    const randomIndex = Math.floor(Math.random() * weightedHeroes.length);
    const selectedHero = weightedHeroes[randomIndex];

    console.log(`[GlobalChatHandler] Selected hero "${selectedHero.name}" (frequency: ${selectedHero.frequency || 6}) from ${heroes.length} available heroes`);
    return selectedHero;
  }

  sendConfirmationEmote(wallet, emote) {
    // Try to find any active scene that can show emotes
    const game = window.phaserGameRef?.current;
    if (game && game.scene) {
      const activeScenes = game.scene.getScenes(true); // Get all active scenes
      for (const scene of activeScenes) {
        if (scene.showEmoteForWallet) {
          scene.showEmoteForWallet(wallet, emote);
          break;
        }
      }
    }
  }
}

// Initialize global handler
export const globalChatHandler = new GlobalChatHandler();
